import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeMode } from '../types/enums';
import { STRING_CONSTANTS } from '../constants/stringConstants';

type ResolvedTheme = 'light' | 'dark';

interface ThemeContextState {
  themeMode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextState | undefined>(undefined);

const getSystemPreference = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const resolveTheme = (mode: ThemeMode, systemPref: ResolvedTheme): ResolvedTheme => {
  if (mode === ThemeMode.LIGHT) return 'light';
  if (mode === ThemeMode.DARK) return 'dark';
  return systemPref;
};

// Apply theme to DOM synchronously to prevent flash
const applyTheme = (theme: ResolvedTheme) => {
  document.documentElement.dataset.theme = theme;
};

// Read stored theme before React renders to prevent flash of wrong theme
const getInitialTheme = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STRING_CONSTANTS.STORAGE_KEYS.THEME);
    if (stored === ThemeMode.LIGHT || stored === ThemeMode.DARK || stored === ThemeMode.AUTO) {
      return stored as ThemeMode;
    }
  } catch {
    // localStorage unavailable
  }
  return ThemeMode.LIGHT;
};

// Apply theme immediately on module load (before React mounts) to prevent flash
const initialMode = getInitialTheme();
const initialSystemPref = getSystemPreference();
applyTheme(resolveTheme(initialMode, initialSystemPref));

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(initialMode);
  const [systemPref, setSystemPref] = useState<ResolvedTheme>(initialSystemPref);

  const resolvedTheme = useMemo(
    () => resolveTheme(themeMode, systemPref),
    [themeMode, systemPref]
  );

  // Listen for system preference changes (for AUTO mode)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setSystemPref(e.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Apply theme to DOM whenever resolved theme changes
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    try {
      localStorage.setItem(STRING_CONSTANTS.STORAGE_KEYS.THEME, mode);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const nextMode = resolvedTheme === 'dark' ? ThemeMode.LIGHT : ThemeMode.DARK;
    setThemeMode(nextMode);
  }, [resolvedTheme, setThemeMode]);

  const value = useMemo<ThemeContextState>(
    () => ({
      themeMode,
      resolvedTheme,
      setThemeMode,
      toggleTheme,
      isDark: resolvedTheme === 'dark',
    }),
    [themeMode, resolvedTheme, setThemeMode, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextState => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
